import type { SecondEarnerRuleSet } from "./types.js";

// second-earner ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// This surface composes two shipped engines rather than re-deriving their math:
//   • income-tax (FY2025-26)  — the second earner's marginal tax + Medicare levy
//     + HELP, resolved by `incomeTaxRulesFor` inside calculate.ts.
//   • au13 CCS (FY2025-26)    — the family's Child Care Subsidy, recomputed at
//     the raised combined income, resolved by `au13RulesFor` inside calculate.ts.
// Neither the brackets nor the CCS schedule are duplicated here.
//
// The ONLY net-new FY-versioned figures this module owns are the FTB Part A
// two-stage income test and the annualisation factor:
//   • FTB-A lower income free area $66,722; max-rate taper 20c/$1 — ledger Fig 3
//     (AGENT_VERIFIED, Services Australia "Income test for FTB Part A", 2025-26).
//   • FTB-A higher income free area $118,771; base-rate taper 30c/$1 — ledger
//     Fig 3 (AGENT_VERIFIED). Both corroborated against the Services Australia
//     page via WebSearch snippet; primary not directly fetchable in-env.
//   • 26 fortnights/year — annualises the au13 fortnightly CCS gap.
//
// The family's own FTB-A max/base annual entitlement is CALLER-SUPPLIED (a
// figure on their Services Australia statement) — we do NOT bake in per-child
// FTB-A rates the ledger did not verify; only the verified thresholds + taper
// rates live here.
//
// Forward note (ledger summary): the 2026-27 CCS BASE threshold rises to $88,520
// (vs $85,279 for 2025-26) and FTB-A free areas reprice annually — author a new
// fyXXXX-XX.ts and register it; never mutate this shipped ruleset.
export const fy2025_26: SecondEarnerRuleSet = {
  engine: "second-earner",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    treasuryWdr:
      "https://treasury.gov.au/sites/default/files/2024-03/p2024-495252-04-incentives.pdf",
    ftbAIncomeTest:
      "https://www.servicesaustralia.gov.au/income-test-for-family-tax-benefit-part?context=22151",
    ccsIncomeTest:
      "https://www.servicesaustralia.gov.au/your-income-can-affect-child-care-subsidy",
  },
  sourcesVerifiedAt: "2026-06-22",

  // FTB Part A two-stage income test, 2025-26 (ledger Fig 3, AGENT_VERIFIED).
  // Max-rate reduces 20c/$1 above $66,722; base-rate reduces 30c/$1 above
  // $118,771.
  ftbA: {
    lowerFreeArea: 66_722,
    lowerTaperRate: 0.2,
    higherFreeArea: 118_771,
    higherTaperRate: 0.3,
  },

  // Standard 26 fortnights per year — annualises the au13 per-fortnight CCS gap.
  fortnightsPerYear: 26,
};
